import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';

import Button from 'components/shared/Button';
import Foldout from 'components/shared/Foldout';
import Search from 'components/shared/Search';
import ControlledTable from 'components/shared/table/ControlledTable';
import { useProfile } from 'context/profile';
import { useDebounce } from 'hooks/useDebounce';
import usePagination from 'hooks/usePagination';
import { DenialList } from 'models/DenialList';
import { DenialListItem } from 'models/DenialListItem';
import { PERMISSIONS } from 'models/Profile';
import { adminDenialListItemQueryKeys, indexAdminDenialListItems } from 'services/api/admin/denialListItems';
import { serializeSort } from 'stores/helpers';
import { colors } from 'styles/theme/colors';
import PlusIcon from 'svg/PlusIcon';

import denialListColumns from '../../denialList/denialListColumns';

// Mirrors the customer Denial List page default (Date Added desc, NULLS LAST server-side).
const DEFAULT_SORT: SortingState = [{ id: 'dateAdded', desc: true }];

type Props = {
  denialList: DenialList;
  onClose: () => void;
};

export default function GlobalDenialListSlideout({ denialList, onClose }: Props) {
  const { profile } = useProfile();
  const [open, setOpen] = useState(false);
  const pagination = usePagination();
  const [sort, setSort] = useState<SortingState>(DEFAULT_SORT);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  useEffect(() => {
    requestAnimationFrame(() => setOpen(true));
  }, []);

  // Reset to the first page on each new search and, while searching, relevance-rank
  // results (sort_by=search) the way the customer Denial List page does.
  useEffect(() => {
    pagination.setPageIndex(0);
    setSort(debouncedSearch ? [{ id: 'search', desc: true }] : DEFAULT_SORT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const animateClose = () => {
    setOpen(false);
    setTimeout(onClose, 300);
  };

  const queryParams = useMemo(
    () => ({
      page: pagination.paging.page,
      pageSize: pagination.paging.pageSize,
      sortBy: serializeSort(sort),
      search: debouncedSearch,
    }),
    [pagination.paging, sort, debouncedSearch]
  );

  const { data, isLoading } = useQuery({
    queryKey: adminDenialListItemQueryKeys.index(denialList.id, queryParams),
    queryFn: ({ signal }) => indexAdminDenialListItems(denialList.id, queryParams, signal),
  });

  // Users who can add patients see the Add New Patient control; view-only users don't. The button
  // itself is a no-op until OLIO-2461 wires up the add flow.
  const canAddItems = profile.has(PERMISSIONS.adminDenialListItemCreate);

  // Narrower Reason column so the item table fits the slideout panel without horizontal scroll
  // (the other columns sum to 500px; the panel's usable width is ~832px). Per-row edit/delete is
  // wired in OLIO-2461; until handlers are passed, denialListColumns omits the menu column.
  const columns = useMemo(() => denialListColumns({ reasonSize: 270 }), []);

  return (
    <Foldout open={open} position='right' onBackdropClick={animateClose} width='min(92%, 880px)'>
      <PanelContainer>
        <PanelHeader>
          <TitleGroup>
            <PanelTitle>{denialList.client?.name} &ndash; Global Denial List</PanelTitle>
            <PatientCount>{denialList.patientCount} patients</PatientCount>
          </TitleGroup>
          <HeaderActions>
            {/* No-op until OLIO-2461 wires the add flow; shown only to users who can add patients. */}
            {canAddItems && (
              <Button variant='primary'>
                <PlusIcon width={16} height={16} />
                Add New Patient
              </Button>
            )}
            <CloseButton onClick={animateClose}>&times;</CloseButton>
          </HeaderActions>
        </PanelHeader>

        <PanelToolbar>
          <SearchContainer>
            <Search value={search} onChange={setSearch} placeholder='Search Patient' />
          </SearchContainer>
        </PanelToolbar>

        <PanelContent>
          <ControlledTable<DenialListItem>
            data={data?.data ?? []}
            columns={columns}
            loading={isLoading}
            pageCount={data?.meta.totalPages ?? 1}
            pagination={pagination}
            sorting={{ sortBy: sort, setSortBy: setSort }}
          />
        </PanelContent>
      </PanelContainer>
    </Foldout>
  );
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0;
`;

const PatientCount = styled.span`
  font-size: 16px;
  color: ${colors.black50};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${colors.black50};
  line-height: 1;
`;

const PanelToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px 0;
`;

const SearchContainer = styled.div`
  width: 280px;
`;

const PanelContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 24px 24px;
`;
